import {
  type FirebaseConfigType,
  sendAnalyticsEvent as firebaseSendEvent,
  initFirebase,
} from './providers/firebase';
import {
  initMixPanel,
  type MixpanelConfigType,
  sendAnalyticsEvent as mixPanelSendEvent,
} from './providers/mixpanel';
import {
  type AnalyticsProviderType,
  AnalyticsProviderTypes,
} from './providerTypes';
import type { AnalyticsEventMap, AnalyticsMeta, AnalyticsUser } from './types';
import { analyticsEventMap } from './types';

type AnalyticsProviderConfig = FirebaseConfigType | MixpanelConfigType;

const sendAnalyticsEvent = (
  providers: AnalyticsProviderType[],
  eventName: string,
  meta?: AnalyticsMeta,
): void => {
  providers.forEach((provider) => {
    if (provider.type === AnalyticsProviderTypes.GOOGLE) {
      firebaseSendEvent(eventName, meta, provider.id);
      return;
    }
    if (provider.type === AnalyticsProviderTypes.MIXPANEL) {
      mixPanelSendEvent(eventName, meta, provider.id);
    }
  });
};

const initAnalytics = ({
  provider,
  configType,
  user,
  providerId,
}: {
  provider: AnalyticsProviderTypes;
  configType: AnalyticsProviderConfig;
  user?: AnalyticsUser;
  providerId?: string;
}): void => {
  if (provider === AnalyticsProviderTypes.GOOGLE) {
    initFirebase({ config: configType as FirebaseConfigType, providerId });
  }
  if (provider === AnalyticsProviderTypes.MIXPANEL) {
    initMixPanel({
      config: configType as MixpanelConfigType,
      user,
      providerId,
    });
  }
};

const analyticsEventHelpers = {
  dashboardWidgetAdd(widgetTitle: string) {
    return `dashboard_widget_add_${widgetTitle}`;
  },
  dashboardWidgetDelete(widgetTitle: string) {
    return `dashboard_widget_delete_${widgetTitle}`;
  },
  dashboardWidgetEdit(widgetTitle: string) {
    return `dashboard_widget_edit_${widgetTitle}`;
  },
  multiCurrencySelectorSelect(currency: string) {
    return `multi_currency_selector_${currency}_select`;
  },
  marketValueChartTimeFrameSelect(timeFrame: string) {
    return `market_value_chart_timeframe_${timeFrame}_select`;
  },
  tabSelect(prefix: string, name: string) {
    return `${prefix}_${name.split(' ').join('_')}_tab_select`;
  },
  modalOpen(modalType: string, context?: string) {
    return `${modalType}_add${context ? `_${context}` : ''}`;
  },
  modalClose(modalType: string, context?: string) {
    return `modal_${modalType}_close${context ? `_${context}` : ''}`;
  },
  modalSubmit(modalType: string, context?: string) {
    return `${modalType}_create${context ? `_${context}` : ''}`;
  },
  editInit(editType: string, context?: string) {
    return `${editType}_edit_init${context ? `_${context}` : ''}`;
  },
  editComplete(editType: string, context?: string) {
    return `${editType}_edit_complete${context ? `_${context}` : ''}`;
  },
  formFieldInteraction(formType: string, fieldName: string, action: string) {
    return `${formType}_${fieldName}_${action}`;
  },
  formCompletion(formType: string, context?: string) {
    return `${formType}_completion${context ? `_${context}` : ''}`;
  },
  formAbandonment(formType: string, context?: string) {
    return `${formType}_abandonment${context ? `_${context}` : ''}`;
  },
  dashboardSave: 'dashboard_save',
  dashboardRearrangeWidgets: 'dashboard_rearrange_widgets',
  dashboardCopyFrom: 'dashboard_copy_from',
} as const;

export const ovAnalyticsEvents: AnalyticsEventMap &
  typeof analyticsEventHelpers = {
  ...analyticsEventMap,
  ...analyticsEventHelpers,
};

export { initAnalytics, sendAnalyticsEvent };
